import { fetchDocumentsByIds } from "@/stores/documents/repository";
import type { MoveDragSelection } from "@/domains/documents/utils/documentMoveDrag";

export interface DroppedDocumentAttachment {
  id: string;
  filename: string;
  state: string | null;
}

export interface AttachDroppedDocumentsResult {
  /** Documents newly attached to the composer. */
  attached: DroppedDocumentAttachment[];
  /** True when the drag carried folders, which the chat cannot attach. */
  skippedFolders: boolean;
  /** Dragged documents that could not be resolved in this building. */
  missing: number;
}

interface AttachDroppedDocumentsParams {
  payload: MoveDragSelection;
  buildingId: string;
  alreadyAttachedIds: readonly string[];
  attach: (doc: DroppedDocumentAttachment) => void;
  /** Injected for tests; defaults to the documents repository. */
  fetchDocuments?: typeof fetchDocumentsByIds;
}

/**
 * Attach documents dragged from the documents list to the chat composer — the
 * same chips the "Add from Franklin" picker creates.
 *
 * The drag payload carries ids only. Filenames and pipeline state come from the
 * database rather than the page that started the drag, so the chip is correct
 * whichever list the row came from, and a document outside the chat's building
 * (the list and the chat are always the same building today, but nothing ties
 * the two together) is refused rather than attached to the wrong conversation.
 *
 * Throws when the lookup fails; the caller reports it.
 */
export async function attachDroppedDocuments({
  payload,
  buildingId,
  alreadyAttachedIds,
  attach,
  fetchDocuments = fetchDocumentsByIds,
}: AttachDroppedDocumentsParams): Promise<AttachDroppedDocumentsResult> {
  const skippedFolders = payload.folderIds.length > 0;
  const already = new Set(alreadyAttachedIds);
  const wanted = [...new Set(payload.docIds.filter((id) => !already.has(id)))];
  const numericIds = wanted.map(Number).filter((id) => Number.isInteger(id) && id > 0);

  if (numericIds.length === 0) {
    return { attached: [], skippedFolders, missing: wanted.length };
  }

  const rows = await fetchDocuments(numericIds, { throwOnError: true });
  // The repository returns newest first; the chips follow the dragged order.
  const byId = new Map(
    rows
      .filter((row) => String(row.building_id) === String(buildingId))
      .map((row) => [row.id, row] as const)
  );
  const attached = numericIds.flatMap((id) => {
    const row = byId.get(id);
    return row ? [{ id: String(row.id), filename: row.filename, state: row.state ?? null }] : [];
  });

  attached.forEach((doc) => attach(doc));
  return { attached, skippedFolders, missing: wanted.length - attached.length };
}
